import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Plus, ChevronLeft, ChevronRight, Eye, EyeOff, X,
  RefreshCw, Soup, Utensils, Clock, Check, BookOpen,
  ChevronDown, Loader2, Pencil, Search, SlidersHorizontal, MapPin,
  Globe, Volume2, VolumeX,
} from "lucide-react";
import { Button, Select, DatePicker, Modal, DriverPaymentModal, OrderBadges } from "../../../components";
import { useAuth } from "../../../context/AuthContext";
import { useOrdersRealtime, useOrderEvents, matchesOrderScope } from "../../../context/RealtimeContext";
import { zapipaj } from "../../../features/pipnutie";
import axios from "axios";
import {
  GET_MANAGER_DAILY_MENU_URL,
  PATCH_MANAGER_DAILY_MENU_URL,
  GET_MANAGER_ORDERS_URL,
  PATCH_ORDER_URL,
  GET_MANAGER_ROUTES_URL,
  GET_ORDER_BALANCE_SNAPSHOT_URL,
} from "../../../features/UrlHelper";
import OrderModal from "./OrderModal";
import AdminEditDeliveredOrderModal from "./AdminEditDeliveredOrderModal";
import OrderItemsModal from "../../../components/OrderItemsModal/OrderItemsModal";
import { ORDER_STATUS_PRIORITY, ITEM_TYPE_META, ORDER_STATUS_META, PAYMENT_TYPE_META } from "../../../features/orderConstants";
import { compareMenuCodesNatural, getComposedMenus, getPriceListTag, getOrderMapsLink } from "../../../features/orderUtils";
import { parseDbUtcDate } from "../../../features/dateFormaters";
import { buildIssuedItemMap, buildOrderedItemMap } from "./dashboardSalesUtils";
import "./DailyMenuSection.scss";
import "./DashboardSection.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
// ORDER_STATUSES – pole odvodené z ORDER_STATUS_META (pre iteráciu v UI)
const ORDER_STATUSES = Object.entries(ORDER_STATUS_META).map(([value, meta]) => ({ value, ...meta }));

const UNAVAIL_PRESETS = [
  { label: "10 min", mins: 10 },
  { label: "20 min", mins: 20 },
  { label: "30 min", mins: 30 },
  { label: "45 min", mins: 45 },
  { label: "1 hod",  mins: 60 },
];

const STATUS_FILTER_OPTIONS = [
  { value: "ALL",       label: <span className="drs__route-option"><span className="drs__route-dot" style={{ background: "var(--c-stav-vsetky)" }} />Všetky</span> },
  { value: "ACCEPTED",  label: <span className="drs__route-option"><span className="drs__route-dot" style={{ background: "var(--c-stav-prijata)" }} />Prijaté</span> },
  { value: "DELIVERY",  label: <span className="drs__route-option"><span className="drs__route-dot" style={{ background: "var(--c-stav-na-trase)" }} />Na trase</span> },
  { value: "DELIVERED", label: <span className="drs__route-option"><span className="drs__route-dot" style={{ background: "var(--c-success)" }} />Doručené</span> },
  { value: "CANCELLED", label: <span className="drs__route-option"><span className="drs__route-dot" style={{ background: "var(--c-error)" }} />Zrušené</span> },
];

// Zdroj objednavky (R3a). Dispecer sa potrebuje pozriet, co mu prislo z webu,
// bez toho, aby to hladal po kartach.
const SOURCE_FILTER_OPTIONS = [
  { value: "ALL",   label: <span className="drs__route-option"><span className="drs__route-dot" style={{ background: "var(--c-stav-vsetky)" }} />Všetky</span> },
  { value: "LOCAL", label: <span className="drs__route-option"><span className="drs__route-dot" style={{ background: "var(--c-neutral-500)" }} />Prevádzka</span> },
  { value: "WEB",   label: <span className="drs__route-option"><span className="drs__route-dot" style={{ background: "var(--c-info)" }} />Web</span> },
];

const DAY_NAMES_SK = ["Pondelok", "Utorok", "Streda", "Štvrtok", "Piatok", "Sobota", "Nedeľa"];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function normalizePickerDate(value, fallback = new Date()) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value === "string") {
    const onlyDate = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (onlyDate) {
      const y = Number(onlyDate[1]);
      const m = Number(onlyDate[2]);
      const d = Number(onlyDate[3]);
      return new Date(y, m - 1, d);
    }
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
  }

  return new Date(fallback.getFullYear(), fallback.getMonth(), fallback.getDate());
}

function formatDateSK(date) {
  return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
}

function getDayNameSK(date) {
  return DAY_NAMES_SK[(date.getDay() + 6) % 7];
}

function getDayCardClass(date) {
  const day = date.getDay();
  const todayStr = toISODate(new Date());
  const dateStr = toISODate(date);
  if (dateStr === todayStr) return "dm-day-card--today";
  if (day === 0 || day === 6) return "dm-day-card--weekend";
  return "";
}

function timeAfterMinutes(mins) {
  const d = new Date(Date.now() + mins * 60_000);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function isTimeValid(timeStr) {
  if (!timeStr) return false;
  const [h, m] = timeStr.split(":").map(Number);
  const sel = new Date();
  sel.setHours(h, m, 0, 0);
  return sel.getTime() >= Date.now() + 9 * 60_000;
}

function statusMeta(val) {
  return { value: val, ...(ORDER_STATUS_META[val] || ORDER_STATUS_META.ACCEPTED) };
}

// ─── SalesLimitsPanel ────────────────────────────────────────────────────────
function SalesLimitHeader() {
  return (
    <div className="slp__header-row">
      <div className="slp__header-empty" aria-hidden="true" />
      <div className="slp__header-values">
        <span className="slp__header-value slp__header-value--ordered">Objednané</span>
        <span className="slp__header-value slp__header-value--issued">Vydané</span>
        <span className="slp__header-value slp__header-value--limit">Limit</span>
      </div>
    </div>
  );
}

function SalesLimitRow({ item, ordered, issued, onUpdateLimit }) {
  const [limitVal, setLimitVal] = useState(item.planned_daily_limit ?? "");
  const inputRef = useRef(null);

  useEffect(() => {
    setLimitVal(item.planned_daily_limit ?? "");
  }, [item.planned_daily_limit]);

  const limit = parseInt(item.planned_daily_limit) || 0;
  const pct = limit > 0 ? Math.min((ordered / limit) * 100, 100) : 0;
  const barClass =
    limit <= 0 ? ""
    : pct >= 90 ? " slp__bar--danger"
    : pct >= 75 ? " slp__bar--warning"
    : "";

  function save() {
    const v = limitVal === "" ? null : parseInt(limitVal);
    if (v !== item.planned_daily_limit) onUpdateLimit(item.id, v);
  }

  return (
    <div className="slp__row">
      <span className="slp__name">
        {item.menu_code && <span className="slp__code-badge">{item.menu_code}</span>}
        <span className="slp__name-text" title={item.name}>{item.name}</span>
      </span>

      <div className="slp__metrics">
        <span className={`slp__metric slp__metric--ordered${pct >= 90 && limit > 0 ? " slp__metric--danger" : pct >= 75 && limit > 0 ? " slp__metric--warning" : ""}`}>
          {ordered}
        </span>
        <span className="slp__metric slp__metric--issued">{issued}</span>
        <div className="slp__limit-wrapper">
          <div className="slp__limit-cell">
            <input
              ref={inputRef}
              className="slp__limit-input"
              type="number"
              min={0}
              value={limitVal}
              placeholder="∞"
              onChange={e => setLimitVal(e.target.value)}
              onBlur={save}
              onKeyDown={e => { if (e.key === "Enter") { save(); inputRef.current?.blur(); } }}
            />
          </div>
          <div
            className="slp__limit-indicator"
            aria-hidden="true"
            style={limit <= 0 ? { visibility: "hidden" } : undefined}
          >
            {limit > 0 && (
              <span className={`slp__limit-indicator-fill${barClass}`} style={{ height: `${pct}%` }} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SalesItemGroup({ type, items, orderedMap, issuedMap, onUpdateLimit }) {
  const meta = ITEM_TYPE_META[type];
  if (!meta || items.length === 0) return null;
  return (
    <div className="slp__category">
      <div className="slp__category-title">{meta.label}</div>
      {items.map(item => (
        <SalesLimitRow
          key={item.id}
          item={item}
          ordered={orderedMap[item.id] || 0}
          issued={issuedMap[item.id] || 0}
          onUpdateLimit={onUpdateLimit}
        />
      ))}
    </div>
  );
}

function SalesLimitsPanel({ menuItems, permanentItems, orders, onUpdateLimit }) {
  const [isExpanded, setIsExpanded] = useState(() => window.innerWidth >= 768);

  const orderedMap = useMemo(() => buildOrderedItemMap(orders), [orders]);
  const issuedMap = useMemo(() => buildIssuedItemMap(orders), [orders]);

  if (menuItems.length === 0 && permanentItems.length === 0) return null;

  function groupByType(items) {
    const groups = {};
    items.forEach(item => {
      const type = item.item_type || "MENU";
      if (!groups[type]) groups[type] = [];
      groups[type].push(item);
    });
    return Object.entries(groups).sort(([a], [b]) => {
      const orderA = ITEM_TYPE_META[a]?.order ?? 99;
      const orderB = ITEM_TYPE_META[b]?.order ?? 99;
      return orderA - orderB;
    });
  }

  const dailyGroups = groupByType(menuItems);
  const permanentGroups = groupByType(permanentItems);

  return (
    <div className="slp">
      <div
        className="slp__header"
        role="button"
        tabIndex={0}
        onClick={() => setIsExpanded(v => !v)}
        onKeyDown={e => e.key === "Enter" && setIsExpanded(v => !v)}
      >
        <span>Predajnosť a limity</span>
        <ChevronDown size={16} className={`slp__chevron${isExpanded ? "" : " slp__chevron--collapsed"}`} />
      </div>
      {isExpanded && (
        <div className="slp__columns">
          <div className="slp__column">
            <div className="slp__column-title">Denné menu</div>
            <SalesLimitHeader />
            {dailyGroups.length === 0
              ? <p className="slp__empty">Žiadne položky</p>
              : dailyGroups.map(([type, items]) => (
                  <SalesItemGroup
                    key={type}
                    type={type}
                    items={items}
                    orderedMap={orderedMap}
                    issuedMap={issuedMap}
                    onUpdateLimit={onUpdateLimit}
                  />
                ))
            }
          </div>
          <div className="slp__column">
            <div className="slp__column-title">Stále menu</div>
            <SalesLimitHeader />
            {permanentGroups.length === 0
              ? <p className="slp__empty">Žiadne položky</p>
              : permanentGroups.map(([type, items]) => (
                  <SalesItemGroup
                    key={type}
                    type={type}
                    items={items}
                    orderedMap={orderedMap}
                    issuedMap={issuedMap}
                    onUpdateLimit={onUpdateLimit}
                  />
                ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ─── DashMenuCard ─────────────────────────────────────────────────────────────
// Uses dm-card classes (from DailyMenuSection.scss) for visual consistency.
// Extends BoardItemCard with a prominent editable "Denný limit" row.
function DashMenuCard({ item, onToggleUnavailable, onUpdateLimit }) {
  const [formOpen, setFormOpen] = useState(false);
  const [untilVal, setUntilVal] = useState("");
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [limitEdit, setLimitEdit] = useState(false);
  const [limitVal, setLimitVal] = useState(item.planned_daily_limit ?? "");
  const limitRef = useRef(null);

  const isUnavail = Boolean(item.unavailable) && (!item.unavailable_until || parseDbUtcDate(item.unavailable_until) > new Date());
  const meta = ITEM_TYPE_META[item.item_type] || ITEM_TYPE_META.MENU;
  const Icon = meta.Icon;

  const untilFormatted = item.unavailable_until
    ? parseDbUtcDate(item.unavailable_until)?.toLocaleString("sk-SK", { dateStyle: "short", timeStyle: "short" }) ?? null
    : null;

  function handlePreset(mins) {
    setSelectedPreset(mins);
    setUntilVal(timeAfterMinutes(mins));
  }

  function markUnavailable(timeStr) {
    let until = null;
    if (timeStr) {
      const [h, m] = timeStr.split(":").map(Number);
      const d = new Date();
      d.setHours(h, m, 0, 0);
      until = d.toISOString();
    }
    onToggleUnavailable(item.id, true, until);
    setFormOpen(false);
    setUntilVal("");
    setSelectedPreset(null);
  }

  function openLimitEdit() {
    setLimitVal(item.planned_daily_limit ?? "");
    setLimitEdit(true);
    setTimeout(() => limitRef.current?.focus(), 50);
  }

  function saveLimitEdit() {
    const v = limitVal === "" ? null : parseInt(limitVal);
    if (v !== item.planned_daily_limit) {
      onUpdateLimit(item.id, v);
    }
    setLimitEdit(false);
  }

  return (
    <div className={`dm-card${isUnavail ? " dm-card--unavailable" : ""}`}>
      {/* ── Main row – identical to BoardItemCard ── */}
      <div className="dm-card__main">
        {item.menu_code && <span className="dm-card__code">{item.menu_code}</span>}
        <Icon size={15} className="dm-card__type-icon" />
        <div className="dm-card__info">
          <span className="dm-card__name">{item.name}</span>
          {item.description && <span className="dm-card__desc">{item.description}</span>}
          {item.allergens_list && (
            <div className="dm-card__allergens">
              {item.allergens_list.split(",").map(n => (
                <span key={n} className="dm-card__allergen-tag">{n.trim()}</span>
              ))}
            </div>
          )}
        </div>
        <div className="dm-card__meta">
          {item.price > 0 && (
            <span className="dm-card__price">{Number(item.price).toFixed(2)} €</span>
          )}

          {/* Limit – inline editable */}
          {limitEdit ? (
            <div className="dm-card__limit-inline" onClick={e => e.stopPropagation()}>
              <input
                ref={limitRef}
                className="dm-card__limit-inline-input"
                type="number"
                min={0}
                value={limitVal}
                placeholder="—"
                onChange={e => setLimitVal(e.target.value)}
                onBlur={saveLimitEdit}
                onKeyDown={e => {
                  if (e.key === "Enter") saveLimitEdit();
                  if (e.key === "Escape") setLimitEdit(false);
                }}
              />
              <button className="dm-card__limit-inline-save" onClick={saveLimitEdit} title="Uložiť">
                <Check size={11} />
              </button>
            </div>
          ) : (
            <button
              className="dm-card__limit-btn"
              onClick={openLimitEdit}
              title="Upraviť denný limit"
            >
              Denný limit: <strong className="dm-card__limit-val">{item.planned_daily_limit ?? "—"}</strong>
            </button>
          )}

          {isUnavail ? (
            <button
              className="dm-card__avail-btn dm-card__avail-btn--restore"
              onClick={() => onToggleUnavailable(item.id, false, null)}
              title="Obnoviť dostupnosť"
            >
              <Eye size={14} />
            </button>
          ) : (
            <button
              className="dm-card__avail-btn"
              onClick={() => setFormOpen(v => !v)}
              title="Označiť ako nedostupné"
            >
              <EyeOff size={14} />
            </button>
          )}
        </div>
      </div>

      {isUnavail && (
        <div className="dm-card__unavail-badge">
          Nedostupné{untilFormatted ? ` do ${untilFormatted}` : " — natrvalo"}
        </div>
      )}

      {formOpen && !isUnavail && (
        <div className="dm-card__unavail-form">
          <div className="dm-card__unavail-top">
            <span className="dm-card__unavail-label">Nedostupné do:</span>
            <div className="dm-card__unavail-presets">
              {UNAVAIL_PRESETS.map(({ label, mins }) => (
                <button
                  key={mins}
                  className={`dm-card__unavail-preset${selectedPreset === mins ? " dm-card__unavail-preset--active" : ""}`}
                  onClick={() => handlePreset(mins)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="dm-card__unavail-row">
            <input
              type="time"
              className="dm-card__unavail-until"
              value={untilVal}
              min={timeAfterMinutes(10)}
              onChange={e => { setSelectedPreset(null); setUntilVal(e.target.value); }}
            />
            <div className="dm-card__unavail-actions">
              <button
                className="dm-card__unavail-confirm"
                onClick={() => markUnavailable(untilVal)}
                disabled={!isTimeValid(untilVal)}
              >Potvrdiť</button>
              <button className="dm-card__unavail-all" onClick={() => markUnavailable(null)}>Natrvalo</button>
              <button className="dm-card__unavail-cancel" onClick={() => setFormOpen(false)}><X size={13} /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── OrderRow ─────────────────────────────────────────────────────────────────
function OrderRow({ order, routes, isAdmin, onPatch, onEdit, onPaymentClick, onEditDelivered, isPastDate }) {
  // `routeModalOpen` je bud true (len zmena trasy), alebo "priradit-a-vydat"
  // -- vtedy sa trasa a stav posielaju v JEDNOM patchi (R34).
  const [routeModalOpen, setRouteModalOpen] = useState(false);
  const [savingRouteId, setSavingRouteId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ open: false, targetStatus: null });
  // Dôvod storna. Pri webovej objednávke je povinný — zákazník sa o zrušení
  // dozvie iba mailom a bez dôvodu mu nezostane nič iné, než zavolať.
  const [dovodStorna, setDovodStorna] = useState("");
  const [itemsModalOpen, setItemsModalOpen] = useState(false);
  const [balanceSnapshot, setBalanceSnapshot] = useState(null);
  const meta = statusMeta(order.status);

  const isDebtPayment = (order.items || []).filter(i => i.quantity > 0).length === 0;

  useEffect(() => {
    if (!isDebtPayment) return;
    axios
      .get(GET_ORDER_BALANCE_SNAPSHOT_URL(order.id))
      .then(({ data }) => setBalanceSnapshot(data.balance_snapshot))
      .catch(() => setBalanceSnapshot(null));
  }, [order.id, isDebtPayment]);
  const StatusIcon = meta.Icon;
  const isLocked = order.status === 'DELIVERED' || order.status === 'CANCELLED';
  const composedMenus = getComposedMenus(order.items || []);
  const priceTag = getPriceListTag(order.kod_cennik);

  // Objednavka na prevadzke sa nenaklada do auta, takze stav "Na trase" pre nu
  // nedava zmysel (R49). Server ho aj tak odmietne -- tu sa tlacidlo rovno
  // zablokuje, aby dispecer nenarazil na chybu.
  const jeOdberNaPrevadzke = order.delivery_type === "PICKUP";

  function handleStatusClick(targetValue) {
    if (isLocked) return;
    if (targetValue === "DELIVERED") {
      onPaymentClick(order);
      return;
    }
    if (targetValue === order.status) return;

    // "Na trase" bez trasy sa nastavit neda (R34). Namiesto chyby sa otvori
    // vyber trasy a po potvrdeni sa posle trasa aj stav naraz.
    if (targetValue === "DELIVERY" && !order.route_id) {
      setRouteModalOpen("priradit-a-vydat");
      return;
    }

    const needsConfirm =
      targetValue === "CANCELLED" ||
      (ORDER_STATUS_PRIORITY[targetValue] !== undefined &&
       ORDER_STATUS_PRIORITY[order.status] !== undefined &&
       ORDER_STATUS_PRIORITY[targetValue] < ORDER_STATUS_PRIORITY[order.status]);
    if (needsConfirm) {
      setConfirmModal({ open: true, targetStatus: targetValue });
    } else {
      onPatch(order.id, { status: targetValue });
    }
  }

  /*
   * Objednavka na prevadzke (R49):
   *   - "Na trase" pre nu nedava zmysel, do auta sa nenaklada,
   *   - "Dorucene" je pre nu "Vydane" -- obsluha jedlo vyda cez pult.
   * Rovnake tlacidla su na karte dvakrat (siroka obrazovka a mobil), takze
   * pravidlo je tu raz a obe kopie si ho beru odtialto.
   */
  const stavNedostupny = (hodnota) => jeOdberNaPrevadzke && hodnota === "DELIVERY";

  const popisStavu = (s) =>
    (jeOdberNaPrevadzke && s.value === "DELIVERED" ? "Vydané" : s.label);

  const titulStavu = (s, isActive) => {
    if (isPastDate) return "História – zmena stavu nie je povolená";
    if (isLocked) return "Doručená/Zrušená – zmena stavu nie je povolená";
    if (stavNedostupny(s.value)) return "Objednávka je na prevádzku – nenakladá sa do auta";
    if (s.value === "DELIVERED") {
      return jeOdberNaPrevadzke
        ? "Vydať objednávku a zapisať platbu"
        : "Označiť ako doručenú a upraviť platbu";
    }
    return isActive ? s.label : `Zmeniť na: ${popisStavu(s)}`;
  };

  const ruseniWebovu = confirmModal.targetStatus === "CANCELLED" && order.source === "WEB";
  const dovodChyba = ruseniWebovu && !dovodStorna.trim();

  function zavriPotvrdenie() {
    setConfirmModal({ open: false, targetStatus: null });
    setDovodStorna("");
  }

  function confirmStatusChange() {
    if (dovodChyba) return;
    const telo = { status: confirmModal.targetStatus };
    if (confirmModal.targetStatus === "CANCELLED" && dovodStorna.trim()) {
      telo.cancel_reason = dovodStorna.trim();
    }
    onPatch(order.id, telo);
    zavriPotvrdenie();
  }

  /*
   * Trasa a spôsob patria k sebe (R58): výdajná trasa znamená odber na
   * prevádzke, rozvozová donášku. Server nesúlad odmietne.
   *
   * Dispečer si preto NEVYBERÁ z osekaného zoznamu — vidí všetky trasy a keď
   * zvolí takú, ktorá k dnešnému spôsobu nesedí, pošle sa spôsob SPOLU s trasou
   * v jednom patchi. Posielať ho inam prepnúť spôsob a potom sa vrátiť vybrať
   * trasu je práca navyše za nič: tým, že trasu vybral, už povedal, čo chce.
   */
  const sposobPreTrasu = (routeId) => {
    const trasa = routes.find((r) => r.id === routeId);
    if (!trasa) return null;
    return trasa.interny_vydaj === true ? "PICKUP" : "DELIVERY";
  };

  async function handleRouteSelect(routeId) {
    const savingKey = routeId || "none";
    setSavingRouteId(savingKey);
    const selectedRoute = routes.find(r => r.id === routeId) || null;
    const localPatch = selectedRoute
      ? { route_name: selectedRoute.name, route_color: selectedRoute.color_code }
      : { route_name: null, route_color: null };

    // Pri "Priradit a vydat na trasu" ide trasa aj stav v JEDNOM patchi (R34).
    // Dva zvlast by pri zlyhani druheho nechali objednavku v stave, ktory si
    // dispecer nevypytal.
    const vydavaNaTrasu = routeModalOpen === "priradit-a-vydat" && routeId;
    const telo = vydavaNaTrasu
      ? { route_id: routeId, status: "DELIVERY" }
      : { route_id: routeId || null };

    // Spôsob ide s trasou len vtedy, keď sa naozaj mení -- inak by sme
    // posielali zmenu, o ktorú nikto nežiadal (a tá mailuje zákazníkovi).
    const novySposob = sposobPreTrasu(routeId);
    if (novySposob && novySposob !== order.delivery_type) {
      telo.delivery_type = novySposob;
      localPatch.delivery_type = novySposob;
    }

    const ok = await onPatch(order.id, telo, vydavaNaTrasu ? { ...localPatch, status: "DELIVERY" } : localPatch);
    setSavingRouteId(null);
    if (ok) setRouteModalOpen(false);
  }

  const { url: mapsUrl, hasPreciseLocation } = getOrderMapsLink(order);
  // Odber na prevadzke bez adresy (zakaznik si ju pri registracii nezadal):
  // v hlavicke namiesto prazdnej pomlcky stoji trasa vydaja, takze dispecer
  // hned vidi, kde jedlo caka.
  const trasaVydaja = !order.customer_address_id && Boolean(order.route_name);

  return (
    <div className={`order-card order-card--${order.status?.toLowerCase()}`}>
      {/* ── Header ── */}
      <div className="order-card__header">
        <div className="order-card__header-left">
          {trasaVydaja ? (
            <span
              className="order-card__trasa-tag"
              style={order.route_color ? { backgroundColor: order.route_color + "22", borderColor: order.route_color + "66", color: order.route_color } : undefined}
            >
              {order.route_color && (
                <span className="order-card__route-dot" style={{ backgroundColor: order.route_color }} />
              )}
              {order.route_name}
            </span>
          ) : order.address_code && (
            <span className="order-card__code">{order.address_code}</span>
          )}
          {trasaVydaja ? null : mapsUrl ? (
            <a
              className="order-card__address order-card__address--link"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Otvoriť v Google Mapy"
              onClick={(e) => e.stopPropagation()}
            >
              {order.delivery_address || "—"}
              {hasPreciseLocation && (
                <MapPin size={13} className="order-card__address-pin" title="Spresnená poloha" />
              )}
            </a>
          ) : (
            <span className="order-card__address">{order.delivery_address || "—"}</span>
          )}
        </div>
        <div className="order-card__header-right">
          <span className={`order-card__price-tag${priceTag.isDefault ? " order-card__price-tag--default" : ""}`} title="Cenník objednávky">
            {priceTag.label}
          </span>
          <span className="order-card__price">{Number(order.total_price || 0).toFixed(2)} €</span>
          {order.status === "ACCEPTED" && isAdmin && !isPastDate && (
            <button
              className="order-card__edit-btn"
              onClick={() => onEdit(order)}
              title="Upraviť objednávku"
            >
              <Pencil size={13} />
            </button>
          )}
          {order.status === "DELIVERED" && isAdmin && (
            <button
              className="order-card__edit-btn"
              onClick={() => onEditDelivered(order)}
              title="Upraviť platobné údaje"
            >
              <Pencil size={13} />
            </button>
          )}
        </div>
      </div>

      {/* ── Body – 3 columns ── */}
      <div className="order-card__body">
        {/* Ľavý blok: Info o zákazníkovi */}
        <div className="order-card__col-customer">
          <span className="order-card__customer-name">
            {[order.customer_name, order.customer_surname].filter(Boolean).join(" ") || "—"}
          </span>
          {/* Odznaky a spresnenie adresy ("3. poschodie, zvonček Novák") patria
              sem, nie do hlavičky: tam sa na mobile bili s adresou a adresa je
              to prvé, čo dispečer číta. */}
          <OrderBadges order={order} />
          {order.address_note && (
            <span className="order-card__address-note">{order.address_note}</span>
          )}
          {order.customer_phone && (
            <a href={`tel:${order.customer_phone}`} className="order-card__customer-phone">
              {order.customer_phone}
            </a>
          )}
          {order.customer_note && (
            <span className="order-card__customer-note">{order.customer_note}</span>
          )}
        </div>

        {/* Stredný blok: Obsah objednávky */}
        <div className="order-card__col-items">
          {isDebtPayment ? (
            <span className="order-card__debt-payment">
              Vyplatenie dlhu
              <span className="order-card__debt-amount">
                {" "}— Splatil: {Number(order.balance || 0).toFixed(2)} € — Zostávajúci dlh: {balanceSnapshot !== null ? Math.abs(balanceSnapshot).toFixed(2) : "…"} €
              </span>
            </span>
          ) : (
            <span
              className="order-card__items order-card__items--clickable"
              onClick={() => setItemsModalOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === "Enter" && setItemsModalOpen(true)}
              title="Zobraziť detail položiek"
            >
              {(order.items || [])
                .filter(i => i.quantity > 0)
                .map((i, idx) => (
                  <span key={idx} className="order-card__item-chip">
                    {i.quantity}x{" "}
                    <strong className="order-card__item-code" title={i.name}>
                      {i.menu_code || i.name}
                    </strong>
                  </span>
                ))}
            </span>
          )}
          {/* Dočasne skryté zložené menu; kód zostáva pripravený na opätovné aktivovanie.
          {composedMenus.length > 0 && (
            <div className="order-card__composed-menus">
              {composedMenus.map(({ label, count }) => (
                <span key={label} className="order-card__composed-badge">
                  <span className="order-card__composed-count">{count}×</span>
                  <strong className="order-card__composed-label">{label}</strong>
                </span>
              ))}
            </div>
          )}
          */}
          {order.dispatcher_note && (
            <span className="order-card__note">{order.dispatcher_note}</span>
          )}
          {order.driver_note && (
            <span className="order-card__driver-note">
              <strong>Poznámka od vodiča:</strong> {order.driver_note}
            </span>
          )}
        </div>

        {/* Pravý blok: Status (desktop), Trasa (tablet) */}
        <div className="order-card__col-actions">
          {/* Status buttons — desktop only, hidden on tablet/mobile */}
          {isAdmin ? (
            <div className="order-card__status-buttons">
              {ORDER_STATUSES.map(s => {
                const isActive = s.value === order.status;
                const SIcon = s.Icon;
                return (
                  <button
                    key={s.value}
                    className={`order-card__status-btn${isActive ? " order-card__status-btn--active" : " order-card__status-btn--inactive"}`}
                    style={{ "--status-color": s.color, "--status-rgb": s.rgb }}
                    onClick={() => handleStatusClick(s.value)}
                    title={titulStavu(s, isActive)}
                    disabled={isPastDate || isLocked || stavNedostupny(s.value)}
                  >
                    <SIcon size={12} />
                    <span>{popisStavu(s)}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <span className="order-card__status-badge" style={{ "--status-color": meta.color, "--status-rgb": meta.rgb }}>
              <StatusIcon size={12} />
              <span>{meta.label}</span>
            </span>
          )}

          {/* Route — admin can change it */}
          {isAdmin ? (
            <button
              className="order-card__route-btn"
              style={order.route_color ? { backgroundColor: order.route_color + "22", borderColor: order.route_color + "66", color: order.route_color } : undefined}
              onClick={() => !isPastDate && !isLocked && setRouteModalOpen(true)}
              disabled={isPastDate || isLocked}
              title={isPastDate ? "História – zmena trasy nie je povolená" : isLocked ? "Doručená/Zrušená – zmena trasy nie je povolená" : undefined}
            >
              {order.route_color && (
                <span className="order-card__route-dot" style={{ backgroundColor: order.route_color }} />
              )}
              {order.route_name || "Trasa"}
            </button>
          ) : (
            order.route_name && (
              <span className="order-card__route-badge">{order.route_name}</span>
            )
          )}
        </div>
      </div>

      {/* ── Status row (bottom of card – tablet/mobile only) ── */}
      <div className="order-card__status-row">
        {/* Status buttons — tablet/mobile only, hidden on desktop */}
        {isAdmin ? (
          <div className="order-card__status-buttons">
            {ORDER_STATUSES.map(s => {
              const isActive = s.value === order.status;
              const SIcon = s.Icon;
              return (
                <button
                  key={s.value}
                  className={`order-card__status-btn${isActive ? " order-card__status-btn--active" : " order-card__status-btn--inactive"}`}
                  style={{ "--status-color": s.color, "--status-rgb": s.rgb }}
                  onClick={() => handleStatusClick(s.value)}
                  title={titulStavu(s, isActive)}
                  disabled={isPastDate || isLocked || stavNedostupny(s.value)}
                >
                  <SIcon size={12} />
                  <span>{popisStavu(s)}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <span className="order-card__status-badge" style={{ "--status-color": meta.color, "--status-rgb": meta.rgb }}>
            <StatusIcon size={12} />
            <span>{meta.label}</span>
          </span>
        )}
      </div>

      {/* ── Payment info (for delivered orders) ── */}
      {order.status === "DELIVERED" && (() => {
        const amountPaid = Number(order.amount_paid || 0);
        const totalPrice = Number(order.total_price || 0);
        const isPartialCash = order.payment_type === "CASH" && amountPaid < totalPrice;
        const isDanger = order.payment_type === "DEBT" || isPartialCash;
        const paymentMeta = PAYMENT_TYPE_META[order.payment_type];
        const PayIcon = paymentMeta?.Icon;

        return (
          <div className={[
            "order-card__payment-info",
            isDanger ? "order-card__payment-info--danger" : ""
          ].filter(Boolean).join(" ")}>
            <span className="order-card__payment-label">Platba:</span>
            <span className="order-card__payment-type">
              {PayIcon && <PayIcon size={13} className="order-card__payment-icon" />}
              {paymentMeta?.label ?? order.payment_type}
            </span>
            {isPartialCash && (
              <span className="order-card__payment-amount order-card__payment-amount--highlighted">
                Splatené: {amountPaid.toFixed(2)} €
              </span>
            )}
          </div>
        );
      })()}

      <OrderItemsModal
        isOpen={itemsModalOpen}
        onClose={() => setItemsModalOpen(false)}
        order={order}
      />

      {/* Confirm status change modal */}
      <Modal
        isOpen={confirmModal.open}
        onClose={zavriPotvrdenie}
        title="Zmena stavu objednávky"
        footer={
          <>
            <Button variant="outline" onClick={zavriPotvrdenie}>Zrušiť</Button>
            <Button variant="primary" onClick={confirmStatusChange} disabled={dovodChyba}>Potvrdiť</Button>
          </>
        }
      >
        <p>
          Naozaj chcete zmeniť stav objednávky na{" "}
          <strong>{confirmModal.targetStatus ? statusMeta(confirmModal.targetStatus).label : ""}</strong>?
          {confirmModal.targetStatus === "CANCELLED" && " Táto akcia môže byť nezvratná."}
        </p>

        {confirmModal.targetStatus === "CANCELLED" && (
          <div className="order-card__storno">
            <label className="order-card__storno-label" htmlFor={`storno-${order.id}`}>
              Dôvod zrušenia{ruseniWebovu ? "" : " (nepovinný)"}
            </label>
            <textarea
              id={`storno-${order.id}`}
              className="order-card__storno-input"
              rows={2}
              maxLength={255}
              value={dovodStorna}
              onChange={(e) => setDovodStorna(e.target.value)}
              placeholder="Napr. Jedlo sa minulo — ospravedlňujeme sa."
            />
            {ruseniWebovu && (
              <p className="order-card__storno-hint">
                Objednávku si zadal zákazník cez web, takže sa o zrušení dozvie iba
                z mailu. Dôvod mu v ňom pošleme.
              </p>
            )}
          </div>
        )}
      </Modal>

      {/* Route select modal */}
      {isAdmin && (
        <Modal
          isOpen={routeModalOpen}
          onClose={() => { if (!savingRouteId) setRouteModalOpen(false); }}
          title={routeModalOpen === "priradit-a-vydat" ? "Objednávka nemá pridelenú trasu" : "Vybrať trasu"}
        >
          {routeModalOpen === "priradit-a-vydat" && (
            <p className="route-modal__hint">
              Vyberte trasu — objednávka sa naň pridelí a rovno označí ako „Na trase“.
            </p>
          )}
          <div className="route-modal__list">
            {routes.length === 0 && (
              <p className="route-modal__hint">Prevádzka zatiaľ nemá žiadnu trasu.</p>
            )}
            {routes.map(r => {
              // Keď zvolená trasa znamená iný spôsob, povie sa to rovno pri nej.
              const sposob = r.interny_vydaj === true ? "PICKUP" : "DELIVERY";
              const prepina = sposob !== order.delivery_type;
              return (
                <button
                  key={r.id}
                  className={`route-modal__item${order.route_id === r.id ? " route-modal__item--active" : ""}`}
                  onClick={() => handleRouteSelect(r.id)}
                  disabled={savingRouteId !== null}
                >
                  <span className="route-modal__dot" style={{ backgroundColor: r.color_code }} />
                  <span className="route-modal__name">
                    {r.name}
                    {prepina && (
                      <small className="route-modal__prepnutie">
                        {sposob === "PICKUP"
                          ? "prepne na odber na prevádzke"
                          : "prepne na donášku"}
                      </small>
                    )}
                  </span>
                  {savingRouteId === r.id && <Loader2 size={14} className="dm__spinner route-modal__spinner" />}
                </button>
              );
            })}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── DashboardSection ─────────────────────────────────────────────────────────
/**
 * Hodiny v hlavicke dashboardu. Samostatny komponent zamerne: stav, ktory sa
 * meni kazdu sekundu, bol v DashboardSection, takze sa kazdu sekundu
 * prekreslil cely dashboard -- vsetky karty objednavok, menu aj limity.
 * Teraz sa prekresli len tento span.
 */
function ZiveHodiny() {
  const [cas, setCas] = useState(() => new Date());

  useEffect(() => {
    const timerId = setInterval(() => setCas(new Date()), 1000);
    return () => clearInterval(timerId);
  }, []);

  return (
    <span className="dashboard-live-clock">
      <Clock size={15} />
      {cas.toLocaleTimeString("sk-SK", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
    </span>
  );
}

function DashboardSection() {
  const { userRole } = useAuth();
  const isAdmin = ["ADMIN", "SUPERADMIN", "MANAGER"].includes(userRole);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [menuItems, setMenuItems] = useState([]);
  const [permanentItems, setPermanentItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [deliveredOrderModalOpen, setDeliveredOrderModalOpen] = useState(false);
  const [editingDeliveredOrder, setEditingDeliveredOrder] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentModalOrder, setPaymentModalOrder] = useState(null);
  const [menuOpen, setMenuOpen] = useState(true);
  const [staleOpen, setStaleOpen] = useState(true);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedRoute, setSelectedRoute] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("ALL");
  const [noveWebove, setNoveWebove] = useState(0);
  // Zvuk si dispečer zapína sám a voľba mu vydrží — na zdieľanom počítači
  // v kuchyni je to vec návyku, nie nastavenia prevádzky.
  const [zvukZapnuty, setZvukZapnuty] = useState(() => {
    try {
      return window.localStorage.getItem("webObjednavkyZvuk") === "1";
    } catch (_) {
      return false;
    }
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const ordersSeqRef = useRef(0);
  const routesSeqRef = useRef(0);


  const dateStr = toISODate(selectedDate);
  const todayStr = toISODate(new Date());
  const isToday = dateStr === todayStr;
  const isPastDate = dateStr < todayStr;

  useEffect(() => {
    loadMenu(dateStr);
    loadOrders(dateStr);
  }, [dateStr]);

  useEffect(() => {
    loadRoutes();
  }, []);

  // ─── Realtime auto-refresh ──────────────────────────────────────────────────
  // Refetch sa odklada, kym je otvoreny modal: OrderModal dostava `orders` cez
  // existingOrders a ostatne modaly drzia snapshot objednavky. Vymena pola pod
  // otvorenym modalom je korektnostne aj UX riziko.
  const anyModalOpen =
    orderModalOpen || !!editingOrder || deliveredOrderModalOpen || paymentModalOpen;
  const modalOpenRef = useRef(anyModalOpen);
  modalOpenRef.current = anyModalOpen;
  const pendingRefetchRef = useRef(false);

  const refetchOrders = useCallback(() => {
    if (modalOpenRef.current) {
      pendingRefetchRef.current = true;
      return;
    }
    loadOrders(dateStr, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr]);

  useEffect(() => {
    if (!anyModalOpen && pendingRefetchRef.current) {
      pendingRefetchRef.current = false;
      loadOrders(dateStr, { silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyModalOpen, dateStr]);

  // Bez filtra na trasu zamerne: dashboard nacitava vsetky trasy a filtruje
  // klientsky cez selectedRoute, takze zmena na lubovolnej trase moze byt
  // prave teraz viditelna.
  useOrdersRealtime({
    filter: (evt) => matchesOrderScope(evt, { date: dateStr }),
    onChange: refetchOrders,
  });

  useOrdersRealtime({
    filter: (evt) => evt.entity === "route" || evt.entity === "resync",
    onChange: loadRoutes,
    debounceMs: 1000,
  });

  /*
   * Upozornenie na novú webovú objednávku (R38).
   *
   * Telefonickú objednávku zadáva dispečer sám, o webovej mu nikto nepovie —
   * a práve tá môže prísť o 11:50. Počíta sa každá zvlášť (`useOrderEvents`),
   * lebo "prišli tri" je iná informácia než "niečo prišlo".
   */
  useOrderEvents({
    filter: (evt) => evt.entity === "order" && evt.action === "create" && evt.source === "WEB",
    onEvent: () => {
      setNoveWebove((n) => n + 1);
      if (zvukZapnuty) zapipaj();
    },
  });

  async function loadMenu(date) {
    setLoadingMenu(true);
    try {
      const resp = await axios.get(GET_MANAGER_DAILY_MENU_URL(date));
      const sortFn = (a, b) => {
        const ao = ITEM_TYPE_META[a.item_type]?.order ?? 9;
        const bo = ITEM_TYPE_META[b.item_type]?.order ?? 9;
        return ao !== bo ? ao - bo : compareMenuCodesNatural(a.menu_code, b.menu_code);
      };
      const dayItems = resp.data
        .filter(item => item.serving_date && toISODate(new Date(item.serving_date)) === date)
        .sort(sortFn);
      const permItems = resp.data
        .filter(item => !item.serving_date)
        .sort(sortFn);
      setMenuItems(dayItems);
      setPermanentItems(permItems);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMenu(false);
    }
  }

  // Seq guard: pri tichom refetchi na pozadi sa moze stat, ze pouzivatel
  // medzitym prepne datum. Odpoved starsieho dopytu potom nesmie prepisat
  // data noveho -- vyhrava vzdy posledne odoslany dopyt.
  async function loadOrders(date, { silent = false } = {}) {
    const seq = ++ordersSeqRef.current;
    if (!silent) setLoadingOrders(true);
    try {
      const resp = await axios.get(GET_MANAGER_ORDERS_URL(date));
      if (seq !== ordersSeqRef.current) return;
      setOrders(resp.data);
    } catch (err) {
      if (seq !== ordersSeqRef.current) return;
      // Zamerne sa NEnuluje zoznam -- pri vypadku siete je stary zoznam
      // uzitocnejsi nez prazdna obrazovka.
      console.error(err);
    } finally {
      if (!silent && seq === ordersSeqRef.current) setLoadingOrders(false);
    }
  }

  async function loadRoutes() {
    const seq = ++routesSeqRef.current;
    try {
      const resp = await axios.get(GET_MANAGER_ROUTES_URL);
      if (seq !== routesSeqRef.current) return;
      setRoutes(resp.data);
    } catch (err) {
      console.error(err);
    }
  }

  async function handleToggleUnavailable(id, unavailable, unavailable_until) {
    try {
      await axios.patch(
        PATCH_MANAGER_DAILY_MENU_URL(id),
        { unavailable, unavailable_until }
      );
      setMenuItems(prev =>
        prev.map(item =>
          item.id === id
            ? { ...item, unavailable, unavailable_until: unavailable_until || null }
            : item
        )
      );
    } catch (err) {
      console.error(err);
    }
  }

  async function handleUpdateLimit(id, planned_daily_limit) {
    try {
      await axios.patch(
        PATCH_MANAGER_DAILY_MENU_URL(id),
        { planned_daily_limit }
      );
      setMenuItems(prev =>
        prev.map(item => item.id === id ? { ...item, planned_daily_limit } : item)
      );
      setPermanentItems(prev =>
        prev.map(item => item.id === id ? { ...item, planned_daily_limit } : item)
      );
    } catch (err) {
      console.error(err);
    }
  }

  async function handlePatchOrder(id, patch, localPatch = null) {
    try {
      await axios.patch(PATCH_ORDER_URL(id), patch);
      setOrders(prev => prev.map(o => o.id === id ? { ...o, ...patch, ...(localPatch || {}) } : o));
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  function handleEditDeliveredOrder(order) {
    setEditingDeliveredOrder(order);
    setDeliveredOrderModalOpen(true);
  }

  function handlePaymentClick(order) {
    setPaymentModalOrder(order);
    setPaymentModalOpen(true);
  }

  function handleDeliveredOrderUpdated(updatedOrder) {
    setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
  }

  // Group menu items by type
  function groupByType(list) {
    return Object.entries(ITEM_TYPE_META)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([type, meta]) => ({ type, meta, items: list.filter(i => i.item_type === type) }))
      .filter(g => g.items.length > 0);
  }
  const menuByType = groupByType(menuItems);
  const permanentByType = groupByType(permanentItems);



  const routeOptions = [
    {
      value: "all",
      label: (
        <span className="drs__route-option">
          <span className="drs__route-dot" style={{ background: "var(--c-stav-vsetky)" }} />
          Všetky trasy
        </span>
      ),
    },
    {
      value: "none",
      label: (
        <span className="drs__route-option">
          <span className="drs__route-dot" style={{ background: "var(--c-error)" }} />
          Bez trasy
        </span>
      ),
    },
    ...routes.map((r) => ({
      value: String(r.id),
      label: (
        <span className="drs__route-option">
          <span className="drs__route-dot" style={{ background: r.color_code }} />
          {r.name}
        </span>
      ),
    })),
  ];

  const normalizedSearchQuery = useMemo(
    () => searchQuery.toLowerCase().trim(),
    [searchQuery]
  );

  const filteredOrders = useMemo(
    () =>
      [...orders]
        .sort((a, b) =>
          (ORDER_STATUS_PRIORITY[a.status] ?? 99) -
          (ORDER_STATUS_PRIORITY[b.status] ?? 99)
        )
        .filter((order) => statusFilter === "ALL" || order.status === statusFilter)
        .filter((order) => {
          // "Bez trasy" je polozka toho isteho vyberu ako trasy -- je to odpoved
          // na tu istu otazku a samostatny prepinac by nutil kombinovat dve
          // ovladania (R3a).
          if (selectedRoute === "all") return true;
          if (selectedRoute === "none") return !order.route_id;
          return String(order.route_id) === selectedRoute;
        })
        .filter((order) => sourceFilter === "ALL" || (order.source || "LOCAL") === sourceFilter)
        .filter((order) => {
          if (!normalizedSearchQuery) return true;

          const customerFullName = [order.customer_name, order.customer_surname]
            .filter(Boolean)
            .join(" ");

          const searchableValues = [
            order.address_code,
            order.delivery_address,
            order.address,
            order.customer_name,
            order.customer_surname,
            customerFullName,
            order.customer_phone,
            order.phone,
          ]
            .filter((value) => value !== null && value !== undefined)
            .map((value) => String(value).toLowerCase());

          return searchableValues.some((value) => value.includes(normalizedSearchQuery));
        }),
    [orders, statusFilter, selectedRoute, sourceFilter, normalizedSearchQuery]
  );

  const orderCount = orders.length;
  const deliveredCount = orders.filter(o => o.status === "DELIVERED").length;
  const hasActiveFilters = statusFilter !== "ALL" || selectedRoute !== "all"
    || sourceFilter !== "ALL" || Boolean(searchQuery.trim());
  const preventHeaderToggle = (event) => {
    event.stopPropagation();
  };



  return (
    <div className="dashboard">
      {/* ── Scrollable body — filter is inside so it shares the same width as cards ── */}
      <div className="dashboard__body">
        {/* Filter bar – sticky at the top of the scroll container */}
        <div className="dm__filter dashboard__filter">
          <button
            className="dm__week-nav"
            onClick={() => setSelectedDate(d => { const n = new Date(d); n.setDate(n.getDate() - 1); return n; })}
            title="Predchádzajúci deň"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="dm__filter-pickers">
            <DatePicker
              label="Dátum"
              value={dateStr}
              onChange={(nextDate) => setSelectedDate((current) => normalizePickerDate(nextDate, current))}
            />
          </div>
          <button
            className="dm__week-nav"
            onClick={() => setSelectedDate(d => { const n = new Date(d); n.setDate(n.getDate() + 1); return n; })}
            title="Nasledujúci deň"
          >
            <ChevronRight size={18} />
          </button>
          <ZiveHodiny />
          {!isPastDate && (
            <Button
              variant="primary"
              onClick={() => setOrderModalOpen(true)}
            >
              <Plus size={16} /><span className="dashboard__new-order-text"> Nová objednávka</span>
            </Button>
          )}
        </div>

        {loadingMenu ? (
          <div className="dm__loading"><Loader2 size={22} className="dm__spinner" /> Načítavam menu…</div>
        ) : (
          <div className="dm-week">

            {/* ── Stav predajnosti a limity ── */}
            {isToday && (
              <SalesLimitsPanel
                menuItems={menuItems}
                permanentItems={permanentItems}
                orders={orders}
                onUpdateLimit={handleUpdateLimit}
              />
            )}

            {/* ── Objednávky (collapsible) ── */}
            <div className="dm-day-card dm-day-card--open">
              <div
                className="dm-day-card__header"
              >
                <div
                  className="dashboard__orders-header-controls flex flex-col gap-3 w-full lg:flex-row lg:items-end lg:justify-between"
                  onClick={preventHeaderToggle}
                  onMouseDown={preventHeaderToggle}
                  onKeyDown={preventHeaderToggle}
                >
                  <div className="dashboard__orders-header-top-row flex flex-row flex-nowrap items-center justify-between w-full lg:w-auto">
                    <h2 className="dm-day-card__name text-red-600 font-medium m-0 text-base sm:text-lg shrink-0 whitespace-nowrap">Objednávky</h2>

                    <span className="dm-day-card__date dashboard__orders-header-date whitespace-nowrap">{deliveredCount}/{orderCount} doručených</span>

                    <div className="dashboard__orders-header-meta flex flex-row flex-nowrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-500 shrink-0">
                      <button
                        type="button"
                        className="dashboard__refresh-btn shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          loadOrders(dateStr);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.stopPropagation();
                            loadOrders(dateStr);
                          }
                        }}
                        title="Obnoviť"
                        aria-label="Obnoviť objednávky"
                      >
                        <RefreshCw size={14} />
                      </button>
                      <div className="dm-day-card__count shrink-0">{orderCount}</div>
                      <button
                        type="button"
                        className={`dashboard__filters-toggle-btn shrink-0${mobileFiltersOpen ? " dashboard__filters-toggle-btn--active" : ""}${hasActiveFilters ? " dashboard__filters-toggle-btn--has-filters" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setMobileFiltersOpen(v => !v);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.stopPropagation();
                            setMobileFiltersOpen(v => !v);
                          }
                        }}
                        title="Filtre"
                        aria-label="Zobraziť alebo skryť filtre objednávok"
                        aria-expanded={mobileFiltersOpen}
                      >
                        <SlidersHorizontal size={14} />
                      </button>
                    </div>
                  </div>

                  <div className={`dashboard__orders-header-filters dashboard__orders-header-bottom-row flex flex-row flex-nowrap items-end gap-2 w-full lg:w-auto lg:flex-1 lg:justify-end overflow-hidden${mobileFiltersOpen ? " dashboard__orders-header-filters--mobile-open" : ""}`}>
                    <div className="dashboard__orders-header-filter flex flex-col flex-1 min-w-[75px] lg:flex-none lg:w-auto">
                      <Select
                        label="Stav"
                        options={STATUS_FILTER_OPTIONS}
                        value={statusFilter}
                        onChange={setStatusFilter}
                      />
                    </div>

                    <div className="dashboard__orders-header-filter flex flex-col flex-1 min-w-[75px] lg:flex-none lg:w-auto">
                      <Select
                        label="Trasa"
                        options={routeOptions}
                        value={selectedRoute}
                        onChange={setSelectedRoute}
                      />
                    </div>

                    <div className="dashboard__orders-header-filter flex flex-col flex-1 min-w-[75px] lg:flex-none lg:w-auto">
                      <Select
                        label="Zdroj"
                        options={SOURCE_FILTER_OPTIONS}
                        value={sourceFilter}
                        onChange={setSourceFilter}
                      />
                    </div>

                    <div className="dashboard__orders-search dashboard__orders-search--header flex-[1.5] min-w-[100px] lg:flex-none lg:max-w-xs" role="search">
                      <Search size={14} className="dashboard__orders-search-icon" aria-hidden="true" />
                      <input
                        type="text"
                        className="dashboard__orders-search-input px-2 text-sm"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Hľadať meno, adresu, telefón..."
                        aria-label="Hľadať objednávku podľa mena, adresy alebo telefónu"
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          className="dashboard__orders-search-clear"
                          onClick={() => setSearchQuery("")}
                          aria-label="Vymazať vyhľadávanie"
                          title="Vymazať vyhľadávanie"
                        >
                          <X size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="dm-day-card__body">
                  {/* Nová webová objednávka (R38). Zmizne až na klik — dispečer
                      môže byť práve pri pulte a toast by mu utečiel. */}
                  {noveWebove > 0 && (
                    <div className="dashboard__nove-web" role="status">
                      <Globe size={16} aria-hidden="true" />
                      <span>
                        {noveWebove === 1
                          ? "Prišla nová objednávka z webu."
                          : `Prišli nové objednávky z webu (${noveWebove}).`}
                      </span>
                      <button
                        type="button"
                        className="dashboard__nove-web-btn"
                        onClick={() => { setSourceFilter("WEB"); setNoveWebove(0); }}
                      >
                        Zobraziť
                      </button>
                      <button
                        type="button"
                        className="dashboard__nove-web-zvuk"
                        onClick={() => {
                          const nove = !zvukZapnuty;
                          setZvukZapnuty(nove);
                          try { window.localStorage.setItem("webObjednavkyZvuk", nove ? "1" : "0"); } catch (_) { /* súkromné okno */ }
                        }}
                        title={zvukZapnuty ? "Vypnúť zvuk upozornenia" : "Zapnúť zvuk upozornenia"}
                      >
                        {zvukZapnuty ? <Volume2 size={15} /> : <VolumeX size={15} />}
                      </button>
                      <button
                        type="button"
                        className="dashboard__nove-web-zavri"
                        onClick={() => setNoveWebove(0)}
                        aria-label="Skryť upozornenie"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  {loadingOrders ? (
                    <div className="dm__loading dm__loading--modal">
                      <Loader2 size={18} className="dm__spinner" /> Načítavam…
                    </div>
                  ) : orders.length === 0 ? (
                    <p className="dm-day-card__empty">Žiadne objednávky pre tento deň.</p>
                  ) : filteredOrders.length === 0 ? (
                    <p className="dm-day-card__empty">Pre zadané filtre sa nenašli žiadne objednávky.</p>
                  ) : (
                    <div className="dashboard__orders-list">
                      {filteredOrders.map(order => (
                          <OrderRow
                            key={order.id}
                            order={order}
                            routes={routes}
                            isAdmin={isAdmin}
                            onPatch={handlePatchOrder}
                            onEdit={o => setEditingOrder(o)}
                            onPaymentClick={handlePaymentClick}
                            onEditDelivered={handleEditDeliveredOrder}
                            isPastDate={isPastDate}
                          />
                        ))}
                    </div>
                  )}
                </div>
            </div>
          </div>
        )}
      </div>

      <OrderModal
        isOpen={orderModalOpen || !!editingOrder}
        onClose={() => { setOrderModalOpen(false); setEditingOrder(null); }}
        defaultDate={selectedDate}
        editOrder={editingOrder}
        existingOrders={orders}
        onOrderCreated={newOrder => {
          setOrders(prev => [newOrder, ...prev]);
          setOrderModalOpen(false);
        }}
        onOrderUpdated={updatedOrder => {
          setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
          setOrderModalOpen(false);
          setEditingOrder(null);
        }}
      />

      <AdminEditDeliveredOrderModal
        isOpen={deliveredOrderModalOpen}
        onClose={() => { setDeliveredOrderModalOpen(false); setEditingDeliveredOrder(null); }}
        order={editingDeliveredOrder}
        onOrderUpdated={handleDeliveredOrderUpdated}
      />

      <DriverPaymentModal
        isOpen={paymentModalOpen}
        onClose={() => {
          setPaymentModalOpen(false);
          setPaymentModalOrder(null);
        }}
        order={paymentModalOrder}
        onSuccess={() => {
          setPaymentModalOpen(false);
          setPaymentModalOrder(null);
          loadOrders(dateStr);
        }}
      />
    </div>
  );
}

// Karta objednavky zvlast -- kvoli testom hlavicky (DashboardSection.test.js).
export { OrderRow };
export default DashboardSection;
